import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { parseFile } from '../utils/pdfParser';
import aiService from '../services/aiService';
import PaymentModal from './PaymentModal';
import OptimizedResumeResults from './OptimizedResumeResults';
import {
  FileText,
  Upload,
  ClipboardList,
  Brain,
  AlertTriangle,
  TrendingUp,
  Star,
  Lock,
  Zap,
  Target,
  Download,
  Eye,
  ArrowRight,
  CheckCircle,
  XCircle,
  CreditCard,
  Sparkles
} from 'lucide-react';

const FreemiumResumeApp = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [uploadMethod, setUploadMethod] = useState('text');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [fullResult, setFullResult] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [error, setError] = useState('');
  const [compressTo2Pages, setCompressTo2Pages] = useState(false);
  const [photo, setPhoto] = useState(null); // New state for photo data

  // Load user subscription on component mount
  useEffect(() => {
    if (user) {
      loadUserSubscription();
    }
  }, [user]);

  // Implement reset on browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = () => {
      resetApp(); // Call resetApp when the browser is refreshed or tab is closed
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount


  const loadUserSubscription = async () => {
    try {
      const response = await aiService.getUserSubscription(user.uid);
      setUserSubscription(response);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setResumeFile(file);
      setError('🔄 Processing file...');
      
      try {
        const extractedText = await parseFile(file);
        if (extractedText && extractedText.length > 20) {
          setResumeText(extractedText);
          setError('');
          
          // Switch to text view to show the extracted content
          setUploadMethod('text');
          
          // Show success message briefly
          setError('✅ PDF processed successfully! Text extracted and ready for analysis.');
          setTimeout(() => setError(''), 3000);
          
          // Focus the textarea after a brief delay to allow DOM update
          setTimeout(() => {
            const textarea = document.querySelector('textarea');
            if (textarea) {
              textarea.focus();
              textarea.scrollTop = 0; // Scroll to top of extracted text
            }
          }, 100);
          
        } else {
          throw new Error('File appears to be empty or contains no readable text.');
        }
      } catch (error) {
        setError(`❌ ${error.message} Please paste your resume text instead.`);
        setResumeFile(null);
        setUploadMethod('text');
      }
    }
  };

  const analyzeResume = async () => {
    if (!resumeText || !jobDescription) {
      setError('Please provide both resume content and job description');
      return;
    }

    setIsAnalyzing(true);
    setCurrentStep(2);
    setError('');

    try {
      // Get preview analysis (free)
      const preview = await aiService.getResumePreview(
        resumeText,
        jobDescription,
        user?.uid || '',
        compressTo2Pages
      );

      setPreviewResult(preview);
      setIsAnalyzing(false);
    } catch (error) {
      setError('Analysis failed: ' + error.message);
      setIsAnalyzing(false);
    }
  };

  const handleUpgrade = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    loadUserSubscription(); // Reload subscription
    proceedWithFullOptimization();
  };

  const proceedWithFullOptimization = async () => {
    setIsAnalyzing(true);
    setCurrentStep(3);

    try {
      const fullOptimization = await aiService.optimizeResume(
        resumeText,
        jobDescription,
        user?.uid || '',
        '', // targetIndustry
        compressTo2Pages,
        photo // Pass the photo data here
      );

      setFullResult(fullOptimization);
      setIsAnalyzing(false);
    } catch (error) {
      setError('Full optimization failed: ' + error.message);
      setIsAnalyzing(false);
    }
  };

  const resetApp = () => {
    console.log('🔄 Resetting app to initial state...');
    
    // Clear all state variables
    setCurrentStep(1);
    setResumeFile(null);
    setResumeText('');
    setJobDescription('');
    setPreviewResult(null);
    setFullResult(null);
    setError('');
    setPhoto(null); // Clear photo on reset
    
    // Clear any optimization session data
    localStorage.removeItem('shortlistly_optimization_session');
    localStorage.removeItem('shortlistly_download_session');
    
    console.log('✅ App reset completed - ready for fresh session');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const handleDownload = async (format, template = 'professional', customResumeData = null) => {
    try {
      if (!fullResult || !fullResult.optimized_resume) {
        setError('No optimization result available for download');
        return;
      }

      console.log('🔄 Downloading with template:', template, 'format:', format);
      console.log('🔍 Full result keys:', Object.keys(fullResult));
      console.log('🔍 Custom resume data:', customResumeData);

      // Use custom resume data if provided (includes photo), otherwise use original
      const structured_data = customResumeData || fullResult.structured_resume_data || {};
      
      console.log('🔍 Final structured data:', structured_data);

      // Prepare request payload with all needed data
      const requestPayload = {
        resume_content: fullResult.optimized_resume,
        template: template,
        structured_data: structured_data
      };

      console.log('📤 Sending to convert API:', requestPayload);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/resume/convert?format=${format}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': format === 'pdf' 
              ? 'application/pdf' 
              : format === 'docx' 
              ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
              : 'text/plain'
          },
          body: JSON.stringify(requestPayload)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Download failed:', response.status, errorText);
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `optimized_resume_${template}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ Download completed successfully');
      
      // Removed call to handleDownloadComplete();
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      setError('Download failed. Please try again.');
    }
  };

  // Removed handleDownloadComplete function

  // Check if user has paid access
  const hasPaidAccess = userSubscription && ['basic', 'premium'].includes(userSubscription.plan_id);

  return (
    <section id="freemium-app" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            AI Resume Optimizer
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Get instant AI analysis of your resume and see exactly what's blocking your job interviews
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                currentStep >= step 
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg' 
                  : 'bg-slate-200 text-slate-500'
              }`}>
                {step}
              </div>
              {step < 3 && (
                <div className={`w-16 h-1 mx-4 transition-all duration-300 ${
                  currentStep > step ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Error/Success Display */}
        {error && (
          <div className={`mb-6 p-4 border rounded-xl text-center font-medium ${
            error.includes('✅') || error.includes('successfully') 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : error.includes('🔄') || error.includes('Processing')
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Input Resume and Job Description */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              <ResumeInputSection
                uploadMethod={uploadMethod}
                setUploadMethod={setUploadMethod}
                resumeText={resumeText}
                setResumeText={setResumeText}
                handleFileUpload={handleFileUpload}
                resumeFile={resumeFile}
                setResumeFile={setResumeFile}
              />
              
              <JobDescriptionSection
                jobDescription={jobDescription}
                setJobDescription={setJobDescription}
                onAnalyze={analyzeResume}
                disabled={!resumeText.trim() || !jobDescription.trim()}
                isAnalyzing={isAnalyzing}
                compressTo2Pages={compressTo2Pages}
                setCompressTo2Pages={setCompressTo2Pages}
              />
            </motion.div>
          )}

          {/* Step 2: Preview Results with Conversion */}
          {currentStep === 2 && previewResult && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <PreviewResultsSection
                result={previewResult}
                onUpgrade={handleUpgrade}
                onReset={resetApp}
                hasPaidAccess={hasPaidAccess}
                onProceedFull={proceedWithFullOptimization}
              />
            </motion.div>
          )}

          {/* Step 3: Full Results with Templates (Paid) */}
          {currentStep === 3 && fullResult && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <OptimizedResumeResults
                fullResult={fullResult}
                onDownload={handleDownload}
                photo={photo} // Pass photo state down
                onPhotoChange={setPhoto} // Pass setter down
                onPhotoRemove={() => setPhoto(null)} // Pass removal handler down
              />
            </motion.div>
          )}

          {/* Loading States */}
          {isAnalyzing && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Brain className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                {currentStep === 2 ? 'Analyzing Your Resume...' : 'Optimizing with AI...'}
              </h3>
              <p className="text-slate-600">
                {currentStep === 2 
                  ? 'Our AI is scanning for critical issues and missing keywords'
                  : 'Creating your optimized resume with advanced AI technology'
                }
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          userId={user?.uid}
        />
      </div>
    </section>
  );
};

// Resume Input Section Component
const ResumeInputSection = ({ 
  uploadMethod, 
  setUploadMethod, 
  resumeText, 
  setResumeText, 
  handleFileUpload,
  resumeFile,
  setResumeFile
}) => (
  <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100">
    <div className="flex items-center mb-6">
      <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
        <FileText className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-slate-900">Your Resume</h3>
    </div>

    {/* Upload Method Toggle */}
    <div className="flex mb-6 bg-slate-100 rounded-2xl p-2">
      <button
        onClick={() => setUploadMethod('text')}
        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
          uploadMethod === 'text' 
            ? 'bg-white text-blue-600 shadow-md' 
            : 'text-slate-600'
        }`}
      >
        <ClipboardList className="w-5 h-5 inline mr-2" />
        Paste Text
      </button>
      <button
        onClick={() => setUploadMethod('file')}
        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
          uploadMethod === 'file' 
            ? 'bg-white text-blue-600 shadow-md' 
            : 'text-slate-600'
        }`}
      >
        <Upload className="w-5 h-5 inline mr-2" />
        Upload File
      </button>
    </div>

    {uploadMethod === 'file' ? (
      <div 
        className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center hover:border-blue-400 transition-colors"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
          
          const files = Array.from(e.dataTransfer.files);
          if (files.length > 0) {
            // Create a synthetic event object for handleFileUpload
            const syntheticEvent = {
              target: { files: files }
            };
            handleFileUpload(syntheticEvent);
          }
        }}
      >
        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-600 mb-4">Drop your resume file here or click to browse</p>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileUpload}
          className="hidden"
          id="resume-upload"
        />
        <label
          htmlFor="resume-upload"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 cursor-pointer transition-colors"
        >
          Choose File
        </label>
        <p className="text-sm text-slate-500 mt-3">Supports PDF, DOC, DOCX, TXT files</p>
      </div>
    ) : (
      <div className="space-y-3">
        {/* Show file upload success indicator */}
        {resumeFile && (
          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-700 font-medium">
                📄 {resumeFile.name} processed successfully
              </span>
            </div>
            <button
              onClick={() => {
                setResumeFile(null);
                setResumeText('');
                setUploadMethod('file');
              }}
              className="text-green-600 hover:text-green-800 font-medium"
            >
              Upload Different File
            </button>
          </div>
        )}
        
        <textarea
          value={resumeText}
          onChange={(e) => setResumeText(e.target.value)}
          className={`w-full h-64 p-4 border rounded-2xl focus:ring-2 focus:border-transparent resize-none transition-all ${
            resumeFile 
              ? 'border-green-300 bg-green-50 focus:ring-green-500' 
              : 'border-slate-300 focus:ring-blue-500'
          }`}
          placeholder={resumeFile ? "Text extracted from your uploaded file. You can edit it here..." : "Paste your complete resume here..."}
        />
      </div>
    )}
  </div>
);

// Job Description Section Component
const JobDescriptionSection = ({ 
  jobDescription, 
  setJobDescription, 
  onAnalyze, 
  disabled, 
  isAnalyzing,
  compressTo2Pages,
  setCompressTo2Pages
}) => (
  <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100">
    <div className="flex items-center mb-6">
      <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
        <Target className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-slate-900">Job Description</h3>
    </div>

    <textarea
      value={jobDescription}
      onChange={(e) => setJobDescription(e.target.value)}
      className="w-full h-64 p-4 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none mb-6"
      placeholder="Paste the job description you're targeting here..."
    />

    {/* 2-Page Compression Option */}
    <div className="bg-blue-50 rounded-2xl p-4 mb-6 border border-blue-200">
      <div className="flex items-start space-x-3">
        <div className="flex items-center mt-1">
          <input
            type="checkbox"
            id="compressTo2Pages"
            checked={compressTo2Pages}
            onChange={(e) => setCompressTo2Pages(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-white border-2 border-blue-300 rounded focus:ring-blue-500 focus:ring-2"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="compressTo2Pages" className="cursor-pointer">
            <div className="text-sm font-semibold text-blue-900 mb-1">
              Automatically shorten your resume to a <strong>concise 2-page format</strong> without losing work experience or skills
            </div>
            <div className="flex items-center text-xs text-blue-700">
              <Brain className="w-3 h-3 mr-1" />
              <span>AI will eliminate wordiness and optimize formatting while preserving all your achievements</span>
            </div>
          </label>
        </div>
      </div>
    </div>

    <button
      onClick={onAnalyze}
      disabled={disabled || isAnalyzing}
      className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold text-lg rounded-2xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isAnalyzing ? (
        <>
          <Brain className="w-6 h-6 mr-3 animate-spin" />
          Analyzing with AI...
        </>
      ) : (
        <>
          <Sparkles className="w-6 h-6 mr-3" />
          Analyze My Resume FREE
        </>
      )}
    </button>
  </div>
);

// Preview Results Section Component
const PreviewResultsSection = ({ 
  result, 
  onUpgrade, 
  onReset, 
  hasPaidAccess,
  onProceedFull 
}) => (
  <div className="space-y-6">
    {/* ATS Score Display */}
    <div className="bg-white rounded-3xl p-6 shadow-xl text-center">
      <div className="mb-4">
        <div className="text-5xl font-bold text-red-500 mb-2">{result.basic_ats_score}%</div>
        <div className="text-lg text-slate-600">ATS Compatibility Score</div>
        <div className="text-red-600 font-semibold mt-2">⚠️ Needs Immediate Improvement</div>
      </div>
    </div>

    {/* Critical Issues (Compact) */}
    <div className="bg-white rounded-3xl p-6 shadow-xl">
      <div className="flex items-center mb-4">
        <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
        <h3 className="text-xl font-bold text-slate-900">Critical Issues Found</h3>
      </div>
      
      <div className="space-y-3 mb-4">
        {result.critical_issues.map((issue, index) => (
          <div key={index} className="flex items-center p-3 bg-red-50 border border-red-200 rounded-xl">
            <XCircle className="w-4 h-4 text-red-500 mr-3" />
            <span className="text-red-700 text-sm">{issue}</span>
          </div>
        ))}
      </div>

      {/* Compact Guilt Messages */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 text-white">
        <h4 className="text-lg font-bold mb-3">Why This Matters:</h4>
        <div className="space-y-1">
          {result.guilt_messages.map((message, index) => (
            <div key={index} className="flex items-start text-sm">
              <span className="mr-2">•</span>
              <span>{message}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Compact Stats & Keywords */}
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white rounded-2xl p-4 shadow-lg">
        <h4 className="text-lg font-bold text-slate-900 mb-3">Missing Keywords</h4>
        <div className="space-y-1">
          {result.missing_keywords.slice(0, 4).map((keyword, index) => (
            <div key={index} className="flex items-center text-sm">
              <Lock className="w-3 h-3 text-orange-500 mr-2" />
              <span className="text-slate-600">{keyword}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-lg">
        <h4 className="text-lg font-bold text-slate-900 mb-3">What We'll Fix</h4>
        <div className="space-y-1">
          {result.improvement_preview.map((improvement, index) => (
            <div key={index} className="flex items-start text-sm">
              {index < 2 ? (
                <CheckCircle className="w-3 h-3 text-blue-500 mr-2 mt-0.5" />
              ) : (
                <Lock className="w-3 h-3 text-orange-500 mr-2 mt-0.5" />
              )}
              <span className={index < 2 ? "text-blue-700" : "text-orange-700 font-semibold"}>
                {improvement}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Action Buttons */}
    <div className="bg-white rounded-3xl p-6 shadow-xl">
      <div className="flex flex-col sm:flex-row gap-4">
        {hasPaidAccess ? (
          <button
            onClick={onProceedFull}
            className="flex-1 flex items-center justify-center px-6 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold rounded-2xl hover:shadow-lg transition-all"
          >
            <Zap className="w-5 h-5 mr-2" />
            Get Full AI Optimization
          </button>
        ) : (
          <button
            onClick={onUpgrade}
            className="flex-1 flex items-center justify-center px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-2xl hover:shadow-lg transition-all"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Unlock Full Optimization - ₹199
          </button>
        )}
        
        <button
          onClick={onReset}
          className="px-6 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-2xl hover:bg-slate-50 transition-all"
        >
          Try Another Resume
        </button>
      </div>
    </div>
  </div>
);

export default FreemiumResumeApp;
